import { useSelector } from 'react-redux';
import { selectStreamingCustomers } from 'redux/reducers/customers';
import { sortByPositionCountOrNamePrecedence } from 'components/data-containers/PositionsTable/helper/uniqueCustomersHelpers';
import { useEffect, Dispatch, SetStateAction, useState } from 'react';
import { IToggleOption } from 'components/data-containers/PositionsTable/TableControls/DSPToggle';
import playlistCustomers from 'maps/customer-name-id';

interface IToggleOptionWithId extends IToggleOption {
  id: string;
}

type TUsePlaylistCustomerResult = [
  IToggleOptionWithId[] | undefined,
  IToggleOptionWithId | undefined,
  Dispatch<SetStateAction<IToggleOptionWithId | undefined>>
];

/**
 * This hook retrieves all the streaming customers from redux and filters them down to a hard-coded list
 * of customers. Apple Music and Spotity are always sorted on the top of this list.
 * The first customer of the list is set as the selected customer by default.
 * Both the customer list and selected customer are returned as stateful objects along with a state setter
 * for the selected customer
 */
const usePlaylistCustomers = (): TUsePlaylistCustomerResult => {
  const [sortedCustomers, setSortedCustomers] = useState<
    IToggleOptionWithId[] | undefined
  >();
  const [selectedCustomer, setSelectedCustomer] = useState<
    IToggleOptionWithId | undefined
  >();

  const streamingCustomers = useSelector(selectStreamingCustomers);
  useEffect(() => {
    const playlistCustomerNames = Object.keys(playlistCustomers);
    const sortedCustomers: IToggleOptionWithId[] = streamingCustomers
      .filter((customer: any) => {
        return playlistCustomerNames.includes(customer.name);
      })
      .map((customer: any) => {
        return {
          id: customer.id.toString(),
          value: customer.name,
          label: customer.displayName,
          positionsCount: 0,
        };
      })
      .sort(sortByPositionCountOrNamePrecedence(['apple music', 'spotify']));

    setSortedCustomers(sortedCustomers);
    setSelectedCustomer(
      sortedCustomers.length ? sortedCustomers[0] : undefined
    );
  }, [streamingCustomers, setSortedCustomers, setSelectedCustomer]);

  return [sortedCustomers, selectedCustomer, setSelectedCustomer];
};

export default usePlaylistCustomers;
